/**
 * Loading a repertoire line whose only tempo marking is a gradual one.
 *
 * Our OSMD fork (1.9.3) cannot read a sheet whose first tempo expression is a gradual
 * change ("calando", "riten.", "accel.", "stretto", "meno mosso" and the like) placed
 * after the first beat, with no fixed tempo before it: its tempo pass adds a starting
 * tempo to a copy of the list, then reads the starting tempo of the gradual mark, which
 * has none, and throws. `load` rejects with "given music sheet was incomplete or could
 * not be loaded". A whole score opens with a tempo, so this never shows there; a line
 * cut from the middle of a piece often starts without one. In the repertoire scene one
 * line that will not load fails the whole piece. (OSMD 1.9.2, which the pipeline's
 * renderer uses, loads these lines.)
 *
 * The fallback adds a hidden metronome mark at the start of the line and loads again.
 * The scene never draws metronome marks and never reads OSMD's tempo (the app keeps
 * its own), so the line looks and plays as written. Lines that load today are loaded
 * exactly as before: the fallback runs only after a failed load.
 */

/** A metronome mark nobody sees: quarter = 60, not printed, on the upper staff. */
const HIDDEN_STARTING_TEMPO_MARK =
  '<direction placement="above"><direction-type>' +
  '<metronome print-object="no"><beat-unit>quarter</beat-unit><per-minute>60</per-minute></metronome>' +
  '</direction-type><staff>1</staff><sound tempo="60"/></direction>';

const FIRST_MEASURE_OPENING_TAG = /<measure\b[^>]*>/;

/**
 * The line with a hidden metronome mark at the start of its first measure, or the line
 * unchanged when it has no measure.
 */
export const withStartingTempoMark = (musicXml: string): string => {
  if (typeof musicXml !== 'string')
    throw new TypeError('withStartingTempoMark: musicXml must be a string');
  const opening = FIRST_MEASURE_OPENING_TAG.exec(musicXml);
  if (!opening) return musicXml;
  const insertAt = opening.index + opening[0].length;
  return musicXml.slice(0, insertAt) + HIDDEN_STARTING_TEMPO_MARK + musicXml.slice(insertAt);
};

/** The part of OSMD this needs: loading a MusicXML string. */
export type MusicXmlLoader = { load: (content: string) => Promise<unknown> };

/**
 * Load the line; if OSMD cannot read it, load it once more with a hidden starting tempo
 * mark. Rejects with the first error when the second load fails too.
 */
export const loadLineWithTempoFallback = async (
  osmd: MusicXmlLoader,
  musicXml: string,
): Promise<'as-written' | 'with-starting-tempo-mark'> => {
  try {
    await osmd.load(musicXml);
    return 'as-written';
  } catch (firstError) {
    const fallback = withStartingTempoMark(musicXml);
    if (fallback === musicXml) throw firstError;
    try {
      await osmd.load(fallback);
    } catch {
      throw firstError;
    }
    return 'with-starting-tempo-mark';
  }
};
